import apiClient from '../api';

export interface TestCaseItem {
  id?: string;
  input: string;
  expected_output: string;
  is_hidden?: boolean;
  order_no?: number;
  explanation?: string;
}

export interface ProblemDataset {
  name: string;
  file_path: string;
  size_bytes?: number;
  mime_type?: string;
  description?: string;
}

export interface StarterCell {
  id: string;
  type: 'code' | 'markdown';
  content: string;
}

export interface NotebookAssertion {
  id?: string;
  name: string;
  assertion_code: string;
  points?: number;
  is_hidden?: boolean;
  explanation?: string;
}

export interface CodingProblemAdminData {
  id: string;
  cohort_id: string;
  title: string;
  slug: string;
  description: string;
  difficulty: 'EASY' | 'MEDIUM' | 'HARD';
  language: string;
  problem_type: 'FUNCTION' | 'STDIN_STDOUT' | 'NOTEBOOK';
  starter_code: string;
  function_name?: string;
  input_format?: string;
  output_format?: string;
  constraints?: string;
  time_limit_ms: number;
  memory_limit_mb: number;
  comparison_type: 'EXACT' | 'WHITESPACE_NORMALIZED' | 'JSON_EQUIVALENT';
  reference_solution?: string;
  status: 'ACTIVE' | 'INACTIVE';
  week_number?: number;
  day_number?: number;
  order_no: number;
  total_test_cases?: number;
  public_test_cases_count?: number;
  hidden_test_cases_count?: number;
  submissions_count?: number;
  test_cases?: TestCaseItem[];
  datasets?: ProblemDataset[];
  starter_cells?: StarterCell[];
  notebook_assertions?: NotebookAssertion[];
  created_at: string;
  updated_at: string;
}

export interface CreateProblemPayload {
  cohort_id?: string;
  title: string;
  slug?: string;
  description: string;
  difficulty?: 'EASY' | 'MEDIUM' | 'HARD';
  language?: string;
  problem_type?: 'FUNCTION' | 'STDIN_STDOUT' | 'NOTEBOOK';
  starter_code?: string;
  function_name?: string;
  input_format?: string;
  output_format?: string;
  constraints?: string;
  time_limit_ms?: number;
  memory_limit_mb?: number;
  comparison_type?: 'EXACT' | 'WHITESPACE_NORMALIZED' | 'JSON_EQUIVALENT';
  reference_solution?: string;
  status?: 'ACTIVE' | 'INACTIVE';
  week_number?: number;
  day_number?: number;
  order_no?: number;
  test_cases?: TestCaseItem[];
  datasets?: ProblemDataset[];
  starter_cells?: StarterCell[];
  notebook_assertions?: NotebookAssertion[];
}

export interface UpdateProblemPayload extends Partial<CreateProblemPayload> {}

export const codingAdminService = {
  getCohortProblems: (cohortId: string) => {
    return apiClient.get<{ error: boolean; status: number; message: string; data: CodingProblemAdminData[] }>(
      `/admin/cohorts/${cohortId}/coding-problems`,
    );
  },

  getProblem: (problemId: string) => {
    return apiClient.get<{ error: boolean; status: number; message: string; data: CodingProblemAdminData }>(
      `/admin/coding-problems/${problemId}`,
    );
  },

  createProblem: (cohortId: string, payload: CreateProblemPayload) => {
    return apiClient.post<{ error: boolean; status: number; message: string; data: CodingProblemAdminData }>(
      `/admin/cohorts/${cohortId}/coding-problems`,
      payload,
    );
  },

  updateProblem: (problemId: string, payload: UpdateProblemPayload) => {
    return apiClient.put<{ error: boolean; status: number; message: string; data: CodingProblemAdminData }>(
      `/admin/coding-problems/${problemId}`,
      payload,
    );
  },

  deleteProblem: (problemId: string) => {
    return apiClient.delete<{ error: boolean; status: number; message: string; data: null }>(
      `/admin/coding-problems/${problemId}`,
    );
  },

  validateReference: (problemId: string) => {
    return apiClient.post<{
      error: boolean;
      status: number;
      message: string;
      data: {
        is_valid: boolean;
        status: string;
        passed_count: number;
        total_count: number;
        total_execution_time_ms: number;
        first_error?: string;
        results: any[];
      };
    }>(`/admin/coding-problems/${problemId}/validate-reference`);
  },

  getProblemSubmissions: (problemId: string) => {
    return apiClient.get<{ error: boolean; status: number; message: string; data: any[] }>(
      `/admin/coding-problems/${problemId}/submissions`,
    );
  },

  revokeSubmission: (submissionId: string) => {
    return apiClient.delete<{ error: boolean; status: number; message: string; data: null }>(
      `/admin/coding-submissions/${submissionId}`,
    );
  },

  uploadDatasetFile: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post<{
      error: boolean;
      status: number;
      message: string;
      data: {
        original_name: string;
        stored_filename: string;
        file_path: string;
        size_bytes: number;
        mime_type: string;
      };
    }>('/admin/datasets/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  deleteDatasetFile: (filename: string) => {
    return apiClient.delete<{ error: boolean; status: number; message: string; data: null }>(
      `/admin/datasets/${filename}`,
    );
  },
};

export default codingAdminService;
